import { ApiErrorCode, type ApiError } from '@musicville/shared';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

export class ApiRequestError extends Error {
  readonly code: ApiErrorCode;
  readonly statusCode: number;
  readonly fieldErrors?: Record<string, string> | undefined;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.code = error.code;
    this.statusCode = error.statusCode;
    this.fieldErrors = error.fieldErrors;
  }

  get isTransient(): boolean {
    return this.statusCode >= 500 || this.code === ApiErrorCode.RATE_LIMITED;
  }
}

interface RefreshedSession {
  accessToken: string;
  [key: string]: unknown;
}

let accessToken: string | null = null;
let refreshPromise: Promise<RefreshedSession | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  retryOnUnauthorised?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, retryOnUnauthorised = true, headers, ...rest } = options;

  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 401 && retryOnUnauthorised && !path.startsWith('/auth/refresh')) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return request<T>(path, { ...options, retryOnUnauthorised: false });
    }
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiRequestError(
      payload && typeof payload === 'object' && 'code' in payload
        ? (payload as ApiError)
        : {
            statusCode: response.status,
            code: ApiErrorCode.INTERNAL,
            message: 'We could not reach the academy just now. Try again in a moment.',
          },
    );
  }

  return payload as T;
}

export function refreshSession(): Promise<RefreshedSession | null> {
  refreshPromise ??= (async () => {
    try {
      const session = await request<RefreshedSession>('/auth/refresh', {
        method: 'POST',
        retryOnUnauthorised: false,
      });
      setAccessToken(session.accessToken);
      return session;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();

  return refreshPromise;
}

export function fieldErrorsOf(error: unknown): Record<string, string> {
  return error instanceof ApiRequestError ? (error.fieldErrors ?? {}) : {};
}

export const api = {
  get: <T>(path: string, init?: RequestOptions) => request<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'POST', body }),
};

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}
